import { springs } from "@/components/ui/motion";
import type { UIAudit } from "@/packages/types";
import { motion } from "framer-motion";
import { Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface StatusTimelineCardProps {
  audit: UIAudit;
  index?: number;
}

type StepKey =
  | "pending"
  | "in_progress"
  | "submitted"
  | "approved"
  | "rejected"
  | "archived";

interface Step {
  key: StepKey;
  label: string;
  timestamp?: number;
}

const standardSteps: StepKey[] = [
  "pending",
  "in_progress",
  "submitted",
  "approved",
];
const terminalSteps: StepKey[] = ["rejected", "archived"];

// Step labels are resolved via useTranslation inside the component

function getTimestamp(audit: UIAudit, step: StepKey): number | undefined {
  switch (step) {
    case "pending":
      return audit.createdAt;
    case "in_progress":
      return undefined;
    case "submitted":
      return audit.submittedAt;
    case "approved":
      return audit.approvedAt;
    case "rejected":
      return audit.rejectedAt;
    case "archived":
      return audit.archivedAt;
    default:
      return undefined;
  }
}

function getStepState(
  currentStatus: string,
  stepKey: StepKey,
  stepIndex: number,
  currentIndex: number,
): "completed" | "current" | "future" {
  if (stepIndex < currentIndex) return "completed";
  if (stepKey === currentStatus) return "current";
  return "future";
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="w-2.5 h-2.5 text-white"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.5 8.5 L6.5 11.5 L12.5 5" />
    </svg>
  );
}

export function StatusTimelineCard({ audit, index }: StatusTimelineCardProps) {
  const { t } = useTranslation("audits");
  const isTerminal = terminalSteps.includes(audit.status as StepKey);

  const getStepLabel = (key: StepKey): string =>
    t(`overview.statusTimeline.steps.${key}`, { defaultValue: key });

  let steps: Step[];
  if (isTerminal) {
    const base = standardSteps.slice(0, -1);
    const allSteps = [...base, audit.status as StepKey];
    steps = allSteps.map((key) => ({
      key,
      label: getStepLabel(key),
      timestamp: getTimestamp(audit, key),
    }));
  } else {
    steps = standardSteps.map((key) => ({
      key,
      label: getStepLabel(key),
      timestamp: getTimestamp(audit, key),
    }));
  }

  const currentIndex = steps.findIndex((s) => s.key === audit.status);

  return (
    <WidgetCard
      size="auto"
      title={t("overview.statusTimeline.title")}
      icon={<Activity size={16} />}
      index={index}
    >
      <ul className="flex flex-col gap-0 h-full list-none m-0 p-0">
        {steps.map((step, i) => {
          const state = getStepState(audit.status, step.key, i, currentIndex);
          const isLast = i === steps.length - 1;

          return (
            <li
              key={step.key}
              className="flex items-start gap-2.5"
              aria-current={state === "current" ? "step" : undefined}
            >
              {/* Circle + line */}
              <div className="flex flex-col items-center">
                {state === "current" ? (
                  <motion.div
                    className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${
                      step.key === "rejected"
                        ? "bg-red-400 ring-4 ring-red-400/20"
                        : step.key === "archived"
                          ? "bg-muted ring-4 ring-muted/20"
                          : "bg-accent ring-4 ring-accent/20"
                    }`}
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{
                      repeat: Number.POSITIVE_INFINITY,
                      duration: 2,
                      ease: "easeInOut",
                    }}
                  />
                ) : (
                  <div
                    className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${
                      state === "completed"
                        ? "bg-emerald-400"
                        : "border-2 border-border"
                    }`}
                  >
                    {state === "completed" && <CheckIcon />}
                  </div>
                )}
                {!isLast && (
                  <motion.div
                    className={`w-0.5 ${
                      state === "completed" ? "bg-emerald-400" : "bg-border"
                    }`}
                    initial={{ height: 0 }}
                    animate={{ height: 12 }}
                    transition={{ ...springs.smooth, delay: 0.1 + i * 0.08 }}
                  />
                )}
              </div>

              {/* Label + timestamp */}
              <div className="flex items-center justify-between flex-1 min-w-0 -mt-0.5">
                <span
                  className={`text-xs ${
                    state === "current"
                      ? "font-medium text-secondary"
                      : state === "completed"
                        ? "text-muted"
                        : "text-muted/60"
                  }`}
                >
                  {step.label}
                </span>
                {step.timestamp && (
                  <span
                    className={`text-xs ${state === "completed" ? "font-medium text-secondary" : "text-muted"}`}
                  >
                    {new Date(step.timestamp).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </WidgetCard>
  );
}

export function StatusTimelineCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="auto" index={index} />;
}
